/**
 *  An iframe that sandboxes wix-dealer ads rendered by our da-wix-dealer app
 *  the app communicates with this component using the postMessage api, telling
 *  it whether an ad was loaded ('ad-filled') or none is available ('ad-empty')
 */
import React, { useCallback, useContext, useState } from 'react';
import classnames from 'classnames';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { useSelectByTheme } from '@wix/da-react-context/pkg/ThemeContext';
import { useMessageListener } from '@wix/da-hooks/pkg/useMessageListener';
import ddt from '@wix/da-ddt';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import s from './DealerAdIframe.scss';

const IFRAME_TITLE = 'DealerAdIframe';
const DEALER_RUNNING_LOCALLY = process.env.NODE_ENV === 'development';
const ADS_ENDPOINT = DEALER_RUNNING_LOCALLY
  ? 'http://localhost:5001/_nsfgfb'
  : 'https://www.deviantart.com/_nsfgfb';
const WIX_DEALER_MESSAGE_TYPE = 'dealerAd';

// this next id can be used to test the system and should always be available
export const TEST_REALESTATEID = 'd4c58bed-9276-439f-b040-36472ccfba3c';

export interface Props {
  // Wix dealer requires an id for any particular ad campaign/set
  realEstateId: string;
  userGuid?: string;
  biClientId?: string;
  biClientIdSigned?: string;
  // start at the full size and hide when there is no ad (true)
  // or start the iframe hidden and show it when/if there's an ad (false)
  showAndCollapse?: boolean;
  className?: string;
  extraParams?: Record<string, string>;
  disableLazy?: boolean;
}

const DealerAdIframe: React.FC<Props> = props => {
  const {
    realEstateId,
    userGuid,
    biClientId,
    biClientIdSigned,
    showAndCollapse,
    className,
    extraParams = {},
    disableLazy,
  } = props;

  const noDealerAds = useFlag('no_dealer_ads'); // do not show ads at all, useful for storybook and such
  const fakeAds = useFlag('yumeruby'); // show a placeholder instead of the iframe, useful for debugging
  const isMounted = useIsMounted();
  const isMobile = useContext(MobileContext);

  const theme = useSelectByTheme({
    dark: 'dark',
    light: 'light',
    green: 'green',
  });

  const [collapsed, setCollapsed] = useState<boolean>(false);
  const handler = useCallback(
    data => {
      ddt.log('dealer', 'Got command from iframe', data);
      switch (data.command) {
        case 'ad-empty':
        case 'ad-filled':
          if (fakeAds) {
            setCollapsed(false);
          } else {
            setCollapsed(
              showAndCollapse ? data.command === 'ad-empty' : data.command !== 'ad-filled'
            );
          }
          break;

        default:
          break;
      }
    },
    [fakeAds, showAndCollapse]
  );
  useWixDealerListener(realEstateId, handler);

  if (noDealerAds) {
    return null;
  }

  const classNames = classnames(s['root'], collapsed && s['collapsed'], className);

  const paramsToSend = new URLSearchParams(
    Object.entries({
      realEstateId,
      theme,
      userGuid,
      biClientId,
      biClientIdSigned,
      newApi: 2,
      platform: isMobile ? 'mobile' : 'desktop',
      ...extraParams,
    }).filter(e => e[1]) as string[][]
  );

  const iframeProps = {
    scrolling: 'no',
    frameBorder: '0',
    style: { border: '0px', width: '100%', height: '100%' } as any,
  };

  if (fakeAds) {
    return (
      <div style={{ background: 'lightskyblue', overflow: 'auto' }} className={classNames}>
        {realEstateId}
      </div>
    );
  }

  if (!isMounted) {
    return null;
  }

  return (
    <iframe
      loading={disableLazy ? 'eager' : 'lazy'}
      className={classNames}
      {...iframeProps}
      title={IFRAME_TITLE}
      src={`${ADS_ENDPOINT}/?${paramsToSend}`}
      allow="fullscreen"
      allowFullScreen
      data-canfs="yes"
    />
  );
};

DealerAdIframe.displayName = 'DealerAdIframe';

// ------------
/** Listen to 'message' events from an iframe containing the da-wix-dealer
 *  app and filtering by a realEstateId */
export function useWixDealerListener(realEstateId: string, handler: Function) {
  useMessageListener(event => {
    if (
      event.data &&
      event.data.type === WIX_DEALER_MESSAGE_TYPE &&
      event.data.realEstateId === realEstateId
    ) {
      handler(event.data);
    }
  });
}

// never, ever re-render the iframe
export default React.memo(DealerAdIframe, () => true);
